export const sleep = (ms: number) =>
    new Promise<void>((resolve) => {
        setTimeout(resolve, ms)
    })

export interface RetryWithBackoffOptions {
    maxRetries?: number
    initialDelayMs?: number
    maxDelayMs?: number
    shouldRetry?: (error: unknown, attempt: number) => boolean
    onRetry?: (error: unknown, attempt: number, delayMs: number) => void
}

const DEFAULT_MAX_RETRIES = 3
const DEFAULT_INITIAL_DELAY_MS = 1000
const DEFAULT_MAX_DELAY_MS = 8000

export function isRetryableHttpError(error: unknown): boolean {
    const status = (error as { response?: { status?: number } })?.response
        ?.status
    if (status == null) {
        return true
    }
    if (status >= 500) {
        return true
    }
    if (status === 408 || status === 429) {
        return true
    }
    return false
}

export async function retryWithBackoff<T>(
    fn: () => Promise<T>,
    options: RetryWithBackoffOptions = {},
): Promise<T> {
    const {
        maxRetries = DEFAULT_MAX_RETRIES,
        initialDelayMs = DEFAULT_INITIAL_DELAY_MS,
        maxDelayMs = DEFAULT_MAX_DELAY_MS,
        shouldRetry = isRetryableHttpError,
        onRetry,
    } = options

    let lastError: unknown
    for (let attempt = 0; attempt <= maxRetries; attempt++) {
        try {
            return await fn()
        } catch (error) {
            lastError = error
            if (attempt >= maxRetries || !shouldRetry(error, attempt)) {
                throw error
            }
            const delayMs = Math.min(initialDelayMs * 2 ** attempt, maxDelayMs)
            onRetry?.(error, attempt + 1, delayMs)
            await sleep(delayMs)
        }
    }

    throw lastError
}
